import { useReducer, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { compose, prop, isNil } from 'ramda'
import effects from 'app/effects/effects'
import {
  mapFromApi,
  mapFromOtherPage,
} from 'app/main/composeMessage/useComposeMapping'
import { mapDraftPayloadToNotification } from 'app/main/composeMessage/mapDraftPayload'
import { useDefaultLanguage } from 'commons/useDefaultLanguage'
import { GENERAL_DIALOG } from 'app/constants/compose'
import { openDialog } from 'app/auth/store/actions/createNotification.actions'
import { UPDATED_MSG } from 'app/constants/messageDetail'
import { useFlag } from '@unleash/proxy-client-react'
import MessageAlert from 'app/components/MessageAlert'
import { useIntl } from 'react-intl'

const SET_NOTIFICATION = 'SET_NOTIFICATION'
const SET_NOTIFICATION_ERROR = 'SET_NOTIFICATION_ERROR'
const CLEAR_STATE = 'CLEAR_STATE'
const START_LOADING = 'START_LOADING'

const initialState = {
  everyone: undefined,
  isLoading: false,
  notification: undefined,
  error: undefined,
}

function reducer(state = '', action) {
  switch (action.type) {
    case SET_NOTIFICATION:
      return {
        ...state,
        notification: action.payload,
        isLoading: false,
      }
    case SET_NOTIFICATION_ERROR:
      return {
        ...state,
        notification: undefined,
        error: action.payload,
        isLoading: false,
      }
    case START_LOADING:
      return {
        ...state,
        isLoading: true,
      }

    case CLEAR_STATE:
      return initialState

    default:
      return state
  }
}

export const useOpenCompose = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const dispatchAction = useDispatch()
  const defaultLanguage = useDefaultLanguage()
  const enableSaveDraftForPolls = useFlag('mes_5281_save_draft_for_polls')
  const { formatMessage } = useIntl()

  const getNotificationById = useCallback(
    ({ notificationId, type }) => {
      dispatch({ type: START_LOADING })

      const apiCall = enableSaveDraftForPolls
        ? effects.apis.getDraftMessage({ id: notificationId })
        : effects.apis.getNotificationById(notificationId)

      return apiCall
        .then(async (result) => {
          const transformedResult = enableSaveDraftForPolls
            ? mapDraftPayloadToNotification(result)
            : result

          const notification = await mapFromApi(
            type,
            transformedResult,
            defaultLanguage,
          )
          dispatch({ type: SET_NOTIFICATION, payload: notification })
        })
        .catch((error) => {
          dispatch({
            type: SET_NOTIFICATION_ERROR,
            payload: error,
          })
          dispatchAction(
            MessageAlert.showError({
              message: formatMessage({
                id: 'error.general',
              }),
            }),
          )
        })
    },
    [defaultLanguage, enableSaveDraftForPolls, dispatchAction, formatMessage],
  )

  const getNotificationByOtherPage = useCallback(
    (result) => {
      return mapFromOtherPage(
        {
          subscribers: compose(isNil, prop('subscribers'))(result)
            ? []
            : result.subscribers,
          subGroups: compose(isNil, prop('subGroups'))(result)
            ? []
            : result.subGroups,
        },
        state,
      )
    },
    [state],
  )

  const onClearState = () => {
    dispatch({ type: CLEAR_STATE })
  }

  const onOpenCompose = useCallback(
    (notificationId, type = GENERAL_DIALOG, handleFetchData, action) => {
      const onFetchData = () => {
        handleFetchData(notificationId, UPDATED_MSG)
      }
      dispatchAction(
        openDialog({
          type,
          initialValues: undefined,
          notificationId,
          onCreateSuccess: onFetchData,
          action,
        }),
      )
    },
    [dispatchAction],
  )

  return {
    state,
    onOpenCompose,
    getNotificationById,
    onClearState,
    getNotificationByOtherPage,
  }
}
